import { useCallback, useContext, useReducer, useRef, useState } from "react";
import { Box, Tab, Tabs } from "@mui/material";
import { Formik } from "formik";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import type { InferType } from "yup";

import type {
  Accreditation,
  ExtendedThalamosUser,
} from "@thalamos/aspire-common";
import {
  isAdminUser,
  isAmhpAccreditation,
  isApprovedClinicianAccreditation,
  isDoctorAccreditation,
  isDoctorS12Searchable,
  isExtendedTeamMembership,
  isHcpcAccreditation,
  isIndependentUserSession,
  isNurseAccreditation,
  isSection12Accreditation,
  isSocialWorkerAccreditation,
  isTeamMembership,
  mergeConfigurations,
} from "@thalamos/aspire-common";

import { api, apiHooks } from "../../api.js";
import {
  LoggedInUserContext,
  ManagedLocationsContext,
} from "../../Contexts.js";
import { useScreenDetection } from "../../hooks/ScreenDetection/useScreenDetection.js";
import {
  autoSaveStateReducer,
  initalAutoSaveState,
} from "../../reducers/autoSaveReducer.js";
import type { DefaultPageProps } from "../defaultProps.js";
import { Section12ProfileInner } from "../Section12Profile/index.js";
import { isS12SetupComplete } from "../Section12Profile/SetupScoreCards.js";
import { UserProfileContainer } from "./UserProfileContainer.js";
import {
  APPROVAL_TYPES,
  createAccreditations,
  dataSchema,
  deriveValues,
  PROF_REG_TYPES,
} from "./userProfileHelpers.js";
import {
  Banner,
  BannerList,
  Button,
  FormTitle,
  LoadingSpinner,
  renderSuccessToast,
} from "~/components/design-system/index.js";
import { FormFooterSection } from "~/components/form/index.js";

const TEAM_TYPES = {
  WARD: "ward",
};

function buildDefaultData(
  profileData: ExtendedThalamosUser,
): InferType<typeof dataSchema> {
  const doctorAccred = profileData.accreditations.find(isDoctorAccreditation);
  const s12Accred = profileData.accreditations.find(isSection12Accreditation);
  const acAccred = profileData.accreditations.find(
    isApprovedClinicianAccreditation,
  );
  const amhpAccred = profileData.accreditations.find(isAmhpAccreditation);
  const nurseAccred = profileData.accreditations.find(isNurseAccreditation);
  const socialWorkerAccreditation = profileData.accreditations.find(
    isSocialWorkerAccreditation,
  );
  const hcpcAccred = profileData.accreditations.find(isHcpcAccreditation);

  const isWardTeamMember = profileData.memberships.find(
    (m) => isTeamMembership(m) && m?.teamType === TEAM_TYPES.WARD,
  );

  const wardTeamType =
    isWardTeamMember &&
    isTeamMembership(isWardTeamMember) &&
    isWardTeamMember?.teamType;

  const occupations = [
    doctorAccred?.type,
    nurseAccred?.type,
    amhpAccred?.type,
  ].filter((o) => o);

  // Annotated wider than the literal values so `.includes` accepts any type.
  const profRegTypes: Accreditation["type"][] = Object.values(PROF_REG_TYPES);
  const approvalTypes: Accreditation["type"][] = Object.values(APPROVAL_TYPES);

  return {
    name: profileData.name,
    address: profileData.address.address,
    postalCode: profileData.address.postalCode,
    defaultSignature: profileData.defaultSignature,

    occupation:
      (occupations.length && occupations) ||
      (wardTeamType && [wardTeamType]) ||
      [],

    teams: profileData.memberships.filter(isTeamMembership),
    profRegistrations: profileData.accreditations
      .map((a) => a.type)
      .filter((type) => profRegTypes.includes(type)),
    mhApprovals: profileData.accreditations
      .map((a) => a.type)
      .filter((type) => approvalTypes.includes(type)),

    gmcNumber: doctorAccred?.gmcNumber,
    pinNumber: nurseAccred?.pinNumber,
    hcpcNumber: hcpcAccred?.registrationNumber,
    socialWorkerNumber: socialWorkerAccreditation?.registrationNumber,

    localAuthority: amhpAccred?.localAuthority,
    localAuthorityOrganisationId: amhpAccred?.localAuthorityOrganisationId,
    approvedClinicianExpiryDate: acAccred?.expiryDate || null,
    section12ExpiryDate: s12Accred?.expiryDate || null,
    section12SyncedWithApprovalsApi: s12Accred?.syncedWithApprovalsApi ?? false,
    approvedClinicianSyncedWithApprovalsApi:
      acAccred?.syncedWithApprovalsApi ?? false,
  };
}

function independentOrganisationId(user: ExtendedThalamosUser): string | null {
  const membership = user.memberships
    .filter(isExtendedTeamMembership)
    .find(
      (m) =>
        m.teamType === "independent" && m.organisationRole === "independent",
    );
  return membership?.organisationId ?? null;
}

export function UserProfileInner({
  user,
  refetchUser,
  isViewedDoctorS12Searchable,
}: {
  user: ExtendedThalamosUser;
  refetchUser: () => void;
  /** Whether the viewed user's independent organisation makes them searchable as an S12 doctor. */
  isViewedDoctorS12Searchable: boolean;
}) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const userContext = useContext(LoggedInUserContext);

  // Determine whether to show S12 profile tabs.
  // Self-view: the logged-in user is an independent S12 doctor with the
  // feature flag enabled on their org configuration.
  // Admin view: the logged-in user is an admin and the *viewed* user has an
  // independent membership + S12 accreditation, and is searchable through
  // their own organisation's configuration.
  const viewedUserHasIndependentMembership =
    independentOrganisationId(user) !== null;
  const viewedUserHasS12Accreditation =
    user.accreditations?.some(isSection12Accreditation) ?? false;

  const selfConfiguration = userContext?.user?.sessionOrganisationConfiguration;
  const isSelfS12User =
    isIndependentUserSession(userContext?.user) &&
    (userContext?.user?.accreditations?.some(isSection12Accreditation) ??
      false) &&
    selfConfiguration != null &&
    isDoctorS12Searchable(selfConfiguration);

  const isAdminViewingS12Doctor =
    isAdminUser(userContext?.user) &&
    viewedUserHasIndependentMembership &&
    viewedUserHasS12Accreditation &&
    isViewedDoctorS12Searchable;

  const isS12User = isSelfS12User || isAdminViewingS12Doctor;

  const S12_SECTIONS = [
    "schedule",
    "location",
    "specialisms",
    "about",
    "contact",
  ] as const;
  const [activeProfileTab, setActiveProfileTab] =
    useState<string>("accreditation");

  const submitFormRef = useRef<(() => Promise<void>) | null>(null);
  const s12SaveNowRef = useRef<(() => Promise<void>) | null>(null);

  const [autoSaveState, dispatchAutoSaveState] = useReducer(
    autoSaveStateReducer,
    initalAutoSaveState,
  );

  const [s12AutoSaveState, dispatchS12AutoSaveState] = useReducer(
    autoSaveStateReducer,
    initalAutoSaveState,
  );

  const onS12AutoSave = useCallback((status: "saving" | "saved" | "error") => {
    if (status === "saving") dispatchS12AutoSaveState("loading");
    else if (status === "saved") dispatchS12AutoSaveState("success");
    else dispatchS12AutoSaveState("error");
  }, []);

  const managedLocationsContext = useContext(ManagedLocationsContext);

  const localAuthorities = managedLocationsContext.managedLocations
    .filter((location) => location.type === "localAuthority")
    .sort((a, b) => a.name.localeCompare(b.name));

  const { isMobileView } = useScreenDetection();

  const onSave = async (userData: InferType<typeof dataSchema>) => {
    const accreditations = createAccreditations(userData);

    // Send API request and refresh data from the server
    await api.users.update(user.id, {
      name: userData.name,
      address: { address: userData.address, postalCode: userData.postalCode },
      defaultSignature: userData.defaultSignature,
      accreditations,
    });
    renderSuccessToast({
      message: `Profile successfully saved`,
    });
    refetchUser();
  };

  const getDefaultSelectedContext = user.memberships.find((m) =>
    m.type === "team"
      ? m.teamId === user.defaultSelectedContext?.id
      : m.organisationId === user.defaultSelectedContext?.id,
  );

  const defaultSelectedContextName =
    getDefaultSelectedContext?.type === "team"
      ? getDefaultSelectedContext.teamName
      : getDefaultSelectedContext?.organisationName;

  return (
    <>
      <FormTitle
        useReducedTopPadding
        hasTitleBottomMargin={false}
        titleText={t("pages.userProfile.title")}
      />
      {/* Outer tab bar */}
      <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 0 }}>
        <Tabs
          value={activeProfileTab}
          onChange={(_, v: string) => setActiveProfileTab(v)}
          data-testid="profile-tab-bar"
        >
          <Tab label="Accreditation" value="accreditation" />
          {isS12User && <Tab label="Schedule" value="schedule" />}
          {isS12User && <Tab label="Location" value="location" />}
          {isS12User && <Tab label="Specialisms" value="specialisms" />}
          {isS12User && <Tab label="About" value="about" />}
          {isS12User && <Tab label="Contact" value="contact" />}
        </Tabs>
      </Box>

      {/* Accreditation tab — kept mounted at all times so Formik state survives tab switches */}
      <Box
        sx={{
          display: activeProfileTab === "accreditation" ? "block" : "none",
        }}
      >
        <>
          <Box sx={{ mb: 2, mt: 2 }}>
            <Banner
              title="Update your profile "
              body={
                "Please enter your professional information and click save. Your profile details will be populated onto the forms you are completing."
              }
            />
          </Box>
          {isS12User && !isS12SetupComplete(user) && (
            <Box sx={{ mb: 2 }} data-testid="s12-setup-nudge-banner">
              <Banner
                bannerType={BannerList.INFO}
                title="Complete your Section 12 profile"
                body="Set up your availability, location and specialisms so AMHPs can find and contact you."
                rightButtonLabel="Section 12 Profile"
                rightButtonTestId="s12-setup-nudge-btn"
                onRightButton={() => setActiveProfileTab("schedule")}
              />
            </Box>
          )}
          <Formik
            validationSchema={dataSchema}
            initialValues={buildDefaultData(user)}
            onSubmit={onSave}
          >
            {({
              values,
              errors,
              touched,
              setValues,
              handleBlur,
              setFieldTouched,
              submitForm,
              /* and other goodies */
            }) => {
              submitFormRef.current = submitForm;
              const fieldProps = {
                validationSchema: dataSchema,
                context: {},
                values,
                errors,
                handleBlur,
                setFieldTouched,
                touched,
                setValues: setValues,
              };
              const derived = deriveValues(values);

              return (
                <>
                  <UserProfileContainer
                    values={values}
                    user={user}
                    errors={errors}
                    setValues={setValues}
                    derived={derived}
                    localAuthorities={localAuthorities}
                    fieldProps={fieldProps}
                    dispatchAutoSaveState={dispatchAutoSaveState}
                    isAutoSaving={autoSaveState.isLoading}
                    defaultSelectedContextName={defaultSelectedContextName}
                  />
                </>
              );
            }}
          </Formik>
        </>
      </Box>

      {/* S12 profile tabs — embedded, shares the outer tab selection */}
      {isS12User &&
        (S12_SECTIONS as readonly string[]).includes(activeProfileTab) && (
          <Section12ProfileInner
            userId={user.id}
            refetchUser={refetchUser}
            embedded
            externalActiveSection={activeProfileTab}
            onActiveSectionChange={setActiveProfileTab}
            onAutoSave={onS12AutoSave}
            viewedUser={user}
            saveNowRef={s12SaveNowRef}
          />
        )}

      {/* Footer — always visible across all tabs */}
      <FormFooterSection
        isSticky={true}
        autoSaveState={
          activeProfileTab === "accreditation"
            ? autoSaveState
            : s12AutoSaveState
        }
        autoSaveType={"profile"}
        discardLabel={t("buttonLabels.close")}
        hideSubmit
        onSave={() => {
          void submitFormRef.current?.();
        }}
        secondaryLeftButton={
          activeProfileTab === "accreditation" ? (
            <Button
              fullWidth={isMobileView}
              variant="contained"
              label={t("buttonLabels.save")}
              onClick={() => {
                void submitFormRef.current?.();
              }}
            />
          ) : (
            <Button
              fullWidth={isMobileView}
              variant="contained"
              label={t("buttonLabels.save")}
              onClick={async () => {
                if (!s12SaveNowRef.current) return;
                try {
                  await s12SaveNowRef.current();
                  renderSuccessToast({
                    message: "Profile successfully saved",
                  });
                } catch {
                  // Auto-save error handling already covers this via
                  // the inline status indicator — no duplicate toast needed.
                }
              }}
            />
          )
        }
        onCancel={() => {
          navigate(-1);
        }}
      />
    </>
  );
}

export function UserProfile(_props: DefaultPageProps) {
  const { userId } = useParams();
  const { t } = useTranslation();
  const userContext = useContext(LoggedInUserContext);

  const [{ data: user, loading: userLoading, error: userError }, refetchUser] =
    apiHooks.users.getUserDetails(userId!);

  // A doctor viewing themselves is judged on their session configuration, so
  // only an admin needs the viewed doctor's organisation fetched.
  const viewedOrganisationId =
    isAdminUser(userContext?.user) && user
      ? independentOrganisationId(user)
      : null;
  const [{ data: viewedOrganisation }] =
    apiHooks.organisations.get(viewedOrganisationId);
  const isViewedDoctorS12Searchable =
    viewedOrganisationId !== null &&
    viewedOrganisation != null &&
    isDoctorS12Searchable(
      mergeConfigurations(
        viewedOrganisationId,
        viewedOrganisation.configurationHierarchy,
      ),
    );

  // Only block-render the spinner on the very first load (no data yet).
  // During subsequent refetches (e.g. after autosave) `userLoading` is true
  // but `user` still holds the previous data — we must NOT unmount
  // UserProfileInner in that case, or its tab state resets to the first tab.
  if (userLoading && !user) {
    return (
      <Box
        sx={{
          height: "100%",
          display: "grid",
          justifyItems: "center",
          alignItems: "center",
        }}
      >
        <LoadingSpinner label="Loading..." />
      </Box>
    );
  }

  if (userError || !user) {
    return <Box>{t("errors.userLoadingErrorMessage")}</Box>;
  }

  return (
    <UserProfileInner
      user={user}
      refetchUser={refetchUser}
      isViewedDoctorS12Searchable={isViewedDoctorS12Searchable}
    />
  );
}
